import {
  type AdminUser,
  AdminUserListSchema,
  AdminUserSchema,
  type CreateAdminUser,
} from '@sbc/domain-models/admin';
import { IntercomTeammateSyncResultSchema } from '@sbc/domain-models/gateway';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGet, apiSend } from './client';

const USERS_KEY = ['admin', 'users'];

export function useAdminUsers() {
  return useQuery<AdminUser[]>({
    queryKey: USERS_KEY,
    queryFn: () => apiGet('/api/admin/users', AdminUserListSchema),
  });
}

// No GET /:id endpoint yet — select the one user out of the cached list.
export function useAdminUser(id: string) {
  return useQuery<AdminUser[], Error, AdminUser | undefined>({
    queryKey: USERS_KEY,
    queryFn: () => apiGet('/api/admin/users', AdminUserListSchema),
    select: (users) => users.find((user) => user.id === id),
  });
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAdminUser) =>
      apiSend('POST', '/api/admin/users', input, AdminUserSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

export function useDeactivateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend('PATCH', `/api/admin/users/${id}/deactivate`, undefined, AdminUserSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

export function useSyncIntercomTeammates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiSend('POST', '/api/admin/intercom-teammates/sync', {}, IntercomTeammateSyncResultSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
}
